/**
 * Easing curves for procedural hold envelopes.
 *
 * Every active procedural hold runs an attack→sustain→release envelope
 * that ramps a per-bone weight from 0 → baseWeight → 0 over the beat's
 * `hold_attack_sec` / `hold_release_sec`. Without easing the ramp would
 * be linear, which reads as mechanical (the body suddenly starts moving
 * at constant speed, then suddenly stops).
 *
 * Different emotions have different *physical signatures*: a grieving
 * character settles into a slumped pose with very soft acceleration and
 * deceleration; an angry character snaps in and snaps out; a panicked
 * character jolts in (near-instant) and slowly recovers. Tempo (duration)
 * and curve shape are independent levers — long duration with linear
 * easing still reads as mechanical, short duration with the right curve
 * reads as organic.
 *
 * We expose four families. Each emotion in `emotion-tempo.ts` maps to one.
 *
 *   gentle  — ease-out quintic both ways. Deeply soft, slow tail. The
 *             body never accelerates; it just decays into the new pose.
 *             For sadness, defeat, tiredness, calm.
 *
 *   smooth  — ease-out cubic both ways. The previous default. Naturalistic
 *             middle-ground. For neutral, joy, triumphant, confused.
 *
 *   snap    — ease-in-out quad both ways. Fast through the middle of the
 *             curve, decisive at both endpoints. Decisive movement, no
 *             lingering. For anger, tense, disgust, excitement.
 *
 *   jolt    — ease-out exponential attack (≈ instant snap), ease-out
 *             cubic release (gradual unwinding). The asymmetry is the
 *             whole point: the body REACTS instantly to a threat or
 *             surprise, then unwinds gradually as the moment passes.
 *             For fear, surprise.
 *
 * Curve outputs are 0 → 1 over t ∈ [0, 1]. The hold envelope multiplies
 * by `baseWeight` (always 1.0 for procedural holds today) to get the
 * applied weight per frame.
 */

export type EasingFamily = 'gentle' | 'smooth' | 'snap' | 'jolt';

export const EASING_FAMILIES: readonly EasingFamily[] = [
  'gentle',
  'smooth',
  'snap',
  'jolt',
] as const;

// ── Curve primitives ──────────────────────────────────────────────────────
//
// All input t is clamped to [0, 1] by callers; these functions assume
// well-formed input. They return values in [0, 1].

/** Ease-out cubic: `1 - (1-t)^3`. The "natural settle" curve. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Ease-out quintic: `1 - (1-t)^5`. Even softer than cubic. */
function easeOutQuintic(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

/**
 * Ease-in-out quadratic: `2t² for t<.5, then 1 - 2(1-t)²`.
 * Symmetric S-curve — fast through middle, decisive at endpoints.
 */
function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Ease-out exponential: `t === 1 ? 1 : 1 - 2^(-10*t)`.
 * Near-instant rise that asymptotes toward 1. Reads as a snap with
 * a faint tail. Used for the attack side of a `jolt` hold so the
 * body REACTS, fast.
 */
function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

// ── Family table ──────────────────────────────────────────────────────────
//
// Each family's `attack` runs forward (0 → 1 as the hold ramps up) and
// `release` runs forward too (0 → 1 as the hold ramps DOWN — the caller
// uses `1 - release(t)` to apply, but expressing the curve as 0→1 keeps
// the math here consistent and inversion happens at the call site).

export interface EasingPair {
  /** Attack curve — input t ∈ [0,1], output the weight multiplier 0 → 1. */
  attack: (t: number) => number;
  /**
   * Release curve — input t ∈ [0,1], output the FALLOFF amount 0 → 1.
   * Caller computes weight as `baseWeight * (1 - release(t))`.
   */
  release: (t: number) => number;
}

export const EASING: Record<EasingFamily, EasingPair> = {
  // Soft acceleration in, soft deceleration out. Long tails on both ends.
  gentle: {
    attack: easeOutQuintic,
    release: easeOutQuintic,
  },
  // Naturalistic. Was the previous default for every hold.
  smooth: {
    attack: easeOutCubic,
    release: easeOutCubic,
  },
  // Decisive S-curve. Fast through the middle, settled at endpoints.
  snap: {
    attack: easeInOutQuad,
    release: easeInOutQuad,
  },
  // Asymmetric: instant attack (body REACTS), gradual release (body
  // unwinds as the moment passes). Pairs with fear/surprise tempo.
  jolt: {
    attack: easeOutExpo,
    release: easeOutCubic,
  },
};

/**
 * Resolve an easing pair, falling back to `smooth` if the family name is
 * unknown or undefined. Used at the figure tick to support beats with no
 * easing field (legacy/Uthana pre-emotion-tempo data).
 */
export function easingForFamily(family?: EasingFamily | null): EasingPair {
  if (family && family in EASING) return EASING[family];
  return EASING.smooth;
}
